/**
 * Producer Service API
 *
 * Service layer for producer-dashboard operations.
 * All requests go through the backoffice client (createApiClient, never raw axios).
 * Endpoint base: /api/v1 (BACKOFFICE base URL).
 */

import backofficeClient from '../clients/backoffice.client';
import { API_ENDPOINTS } from '../config';
import type {
  ProducerResponse,
  ProducerListResponse,
  CreateProducerRequest,
  AssignedBooksResponse,
  AssignedBooksFilters,
  MarkCompletedRequest,
  MarkCompletedResponse,
} from '../types/producer.types';

export const producerService = {
  /**
   * List all producers (backoffice producer-management section + assign/reassign picker).
   * Endpoint: GET /producers
   * Requires role ARABOOKVERSE_BACKOFFICE.
   * Returns all producers when status is omitted; PENDING values included.
   */
  async getProducers(status?: 'PENDING' | 'ACTIVE' | 'INACTIVE'): Promise<ProducerResponse[]> {
    const response = await backofficeClient.get<ProducerListResponse>(
      API_ENDPOINTS.BACKOFFICE.PRODUCERS_LIST,
      { params: status ? { status } : undefined }
    );
    return response.data?.producers ?? [];
  },

  /**
   * Create a new PENDING producer (orchestration path, backoffice-driven).
   * Endpoint: POST /producers
   * Requires role ARABOOKVERSE_BACKOFFICE.
   * Body: { name, email } — browser path only; backend calls auth invite internally.
   * Returns 201 on creation; 409 if email already registered as a producer.
   */
  async createProducer(data: CreateProducerRequest): Promise<ProducerResponse> {
    const response = await backofficeClient.post<ProducerResponse>(
      API_ENDPOINTS.BACKOFFICE.PRODUCERS_LIST,
      data
    );
    return response.data;
  },

  /**
   * List producers (assign/reassign picker source).
   * Endpoint: GET /producers
   * Returns producer-entity records — producerId is what assign/reassign require.
   * @deprecated Use getProducers() instead; kept for backward compat with production hooks.
   */
  async listProducers(status?: 'ACTIVE' | 'INACTIVE'): Promise<ProducerResponse[]> {
    const response = await backofficeClient.get<ProducerListResponse>(
      API_ENDPOINTS.BACKOFFICE.PRODUCERS_LIST,
      { params: status ? { status } : undefined }
    );
    return response.data?.producers ?? [];
  },

  /**
   * Resolve producer profile by auth userId.
   * Endpoint: GET /producers/user/{userId}
   */
  async getProducerByUserId(userId: string): Promise<ProducerResponse> {
    const response = await backofficeClient.get(
      API_ENDPOINTS.BACKOFFICE.PRODUCER_BY_USER_ID(userId)
    );
    return response.data;
  },

  /**
   * Get producer's assigned books (paginated, filterable).
   * Endpoint: GET /producers/{producerId}/assigned-books
   */
  async getAssignedBooks(
    producerId: string,
    filters?: AssignedBooksFilters
  ): Promise<AssignedBooksResponse> {
    const response = await backofficeClient.get(
      API_ENDPOINTS.BACKOFFICE.PRODUCER_ASSIGNED_BOOKS(producerId),
      { params: filters }
    );
    return response.data;
  },

  /**
   * Mark one or more books as production-completed.
   * Endpoint: POST /producers/{producerId}/assigned-books/mark-completed
   * Partial-success: check results[] for per-book outcome.
   * Returns 200; partial failures are in results where status !== COMPLETED.
   */
  async markBooksCompleted(
    producerId: string,
    body: MarkCompletedRequest
  ): Promise<MarkCompletedResponse> {
    const response = await backofficeClient.post(
      API_ENDPOINTS.BACKOFFICE.PRODUCER_MARK_COMPLETED(producerId),
      body
    );
    return response.data;
  },
};

export default producerService;
